import { useCallback, useEffect, useMemo, useState } from 'react';

export interface UseCapAwareMultiSelectParams {
  /** Currently visible domain rows (after query + excludedDomains
   *  filtering). The hook reads this to decide whether everything is
   *  already selected and to walk the bulk-add path. */
  visibleDomains: string[];
  /** Number of sources already added to the collection. Combined with
   *  `cap` this gives the running "how many slots are free?" math. */
  excludedCount: number;
  /** Per-collection ceiling for total selected sources. When undefined,
   *  cap enforcement is disabled — `togglePending` and
   *  `handleSelectAllVisible` never refuse a click and the destructive
   *  flag never fires. */
  cap: number | undefined;
  /** Whether the dropdown panel is currently open. When this flips to
   *  false we clear both `pending` and `capError` so reopening the
   *  panel doesn't resurface stale state from a dismissed session. */
  isOpen: boolean;
}

export interface UseCapAwareMultiSelectReturn {
  pending: Set<string>;
  togglePending: (domain: string) => void;
  handleSelectAllVisible: () => void;
  handleClearPending: () => void;
  /** Drop the pending set without touching `capError` — used by the
   *  submit path which leaves the dropdown in a known-clean state. */
  resetPending: () => void;
  allVisibleSelected: boolean;
  /** True iff bulk-adding every visible not-yet-pending row would fit
   *  within the cap (or when no cap is in effect). Drives whether the
   *  "Select all (N)" CTA is even shown — otherwise it presents a
   *  misleading bulk action when only a few of the N slots remain. */
  canSelectAllVisible: boolean;
  /** Slots free for additional selection (cap - excluded - pending),
   *  or `null` when no cap is in effect. */
  remaining: number | null;
  /** True iff a toggle or Select-all was refused this session OR the
   *  collection itself is already at capacity. Drives the destructive
   *  caption surfaced by the caller. */
  showCapError: boolean;
}

/**
 * Owns the pending-selection set for the multi-select source dropdown,
 * with optional per-collection cap enforcement.
 *
 * Lives outside `SourceSearchInput` so the layout component stays under
 * the codebase-wide 300-line cap (which it would otherwise exceed once
 * cap-awareness is folded in). The hook also makes the rules
 * independently testable from the rendered surface — toggling, bulk
 * add, cap refusal, and auto-clear of `capError` all live in one place.
 */
export function useCapAwareMultiSelect({
  visibleDomains,
  excludedCount,
  cap,
  isOpen,
}: UseCapAwareMultiSelectParams): UseCapAwareMultiSelectReturn {
  const [pending, setPending] = useState<Set<string>>(() => new Set());
  // `capError` flips to true the moment a toggle or Select-all is
  // refused because the per-collection ceiling has been reached. We
  // keep it as a sticky flag (rather than re-deriving on every render)
  // so the destructive caption stays visible long enough to be read
  // even after the offending click already executed its
  // `preventDefault`. Cleared on dropdown close and whenever the user
  // reduces pending below the cap.
  const [capError, setCapError] = useState(false);

  // Reset pending selection whenever the dropdown closes without an
  // explicit submit — covers click-outside and Escape dismissal so
  // reopening doesn't resurface stale selections that may no longer be
  // in the visible list. Clearing `capError` here too keeps the error
  // surface scoped to the current open session.
  useEffect(() => {
    if (!isOpen) {
      setPending(new Set());
      setCapError(false);
    }
  }, [isOpen]);

  // Auto-clear `capError` once the user has freed up at least one slot
  // — typically by un-toggling a row. Without this the destructive
  // caption would persist even after the user resolved the condition
  // that triggered it, which reads as a stale warning.
  useEffect(() => {
    if (!capError || cap === undefined) return;
    const slots = cap - excludedCount - pending.size;
    if (slots > 0) setCapError(false);
  }, [capError, cap, excludedCount, pending.size]);

  const togglePending = useCallback(
    (domain: string) => {
      setPending((prev) => {
        const next = new Set(prev);
        // Un-toggling is always free — it only frees a slot.
        if (next.has(domain)) {
          next.delete(domain);
          return next;
        }
        // Block the add when committing it would push the running
        // total (already-added + pending) past the cap. `>=` (rather
        // than `>`) because `next.size` is measured BEFORE the
        // would-be add.
        if (cap !== undefined && next.size + excludedCount >= cap) {
          setCapError(true);
          return prev;
        }
        next.add(domain);
        return next;
      });
    },
    [cap, excludedCount]
  );

  const allVisibleSelected = useMemo(
    () => visibleDomains.length > 0 && visibleDomains.every((d) => pending.has(d)),
    [visibleDomains, pending]
  );

  const handleSelectAllVisible = useCallback(() => {
    setPending((prev) => {
      // Decide direction from the latest committed state, not a
      // captured snapshot — this is concurrent-safe under React 18
      // where the updater can be replayed against newer state than
      // the closure that scheduled it.
      const allSelected =
        visibleDomains.length > 0 && visibleDomains.every((d) => prev.has(d));
      const next = new Set(prev);
      if (allSelected) {
        for (const d of visibleDomains) next.delete(d);
        return next;
      }
      // Bulk-add path: walk visible domains in order and only add up
      // to the cap. Dropping silently would defeat the whole point of
      // the per-collection ceiling — so we record `dropped` and flip
      // `capError` if any visible row had to be skipped.
      let dropped = 0;
      for (const d of visibleDomains) {
        if (next.has(d)) continue;
        if (cap !== undefined && next.size + excludedCount >= cap) {
          dropped++;
          continue;
        }
        next.add(d);
      }
      if (dropped > 0) setCapError(true);
      return next;
    });
  }, [visibleDomains, cap, excludedCount]);

  const handleClearPending = useCallback(() => {
    setPending(new Set());
    setCapError(false);
  }, []);

  const resetPending = useCallback(() => {
    setPending(new Set());
  }, []);

  // Cap-derived surface flags. `remaining` reflects the live slot
  // count; `showCapError` covers BOTH user-attempted over-selection
  // (`capError`) and the steady-state "dead-end open" case where the
  // collection itself already meets or exceeds the cap before any
  // pending selection happened.
  const remaining =
    cap !== undefined ? Math.max(0, cap - excludedCount - pending.size) : null;
  const alreadyFull = cap !== undefined && excludedCount >= cap;
  const showCapError = cap !== undefined && (capError || alreadyFull);

  // `canSelectAllVisible` reports whether bulk-adding every visible
  // not-yet-pending row would fit. With no cap it's always true; with
  // a cap, it's the slot-fit check used to hide the "Select all (N)"
  // CTA when N would silently truncate. The "Deselect all" toggle is
  // still surfaced separately via `allVisibleSelected`.
  const visibleNotPendingCount = visibleDomains.reduce(
    (acc, d) => acc + (pending.has(d) ? 0 : 1),
    0
  );
  const canSelectAllVisible = remaining === null || visibleNotPendingCount <= remaining;

  return {
    pending,
    togglePending,
    handleSelectAllVisible,
    handleClearPending,
    resetPending,
    allVisibleSelected,
    canSelectAllVisible,
    remaining,
    showCapError,
  };
}
